import { RouterOutputs } from 'api'
import { useCallback, useMemo } from 'react'
import { FaceMeasurements, OrderValues } from '@ca/common/types'
import FrameItem from '@frame/Frame'
import { OrderFrame } from '@ca/common'
import { useTranslation } from 'react-i18next'
import Dropdown from '@ui/elements/Dropdown'
import { useAdjustedFrame } from '@frame/useAdjustedFrame'
import React from 'react'
import { statusToVariant } from '@frame/Frame/styles'

type FrameListItem = RouterOutputs['frames']['customer']['list']['data'][number]

interface Props {
  data: FrameListItem
  isSelected: boolean
  onSelect: (frame: OrderFrame) => void
  onRemove: (id: string) => void
  orderValues: OrderValues
}

export function FrameSelectItem(
  props: Omit<Props, 'data' | 'selectedId'> & {
    frame: OrderFrame
    id: string
    isSelected: boolean
  }
) {
  const { t } = useTranslation()

  const { frame, id, onSelect, onRemove, isSelected, orderValues } = props

  const onPress = useCallback(() => {
    onSelect(frame)
  }, [frame, onSelect])

  return (
    <FrameItem
      frame={frame}
      checked={isSelected}
      onPress={onPress}
      status={isSelected ? 'selected' : undefined}
      variant={isSelected ? statusToVariant['selected'] : undefined}
      title={frame.attributes.name || frame.default_code}
      image={frame.url}
      orderValues={orderValues}
      dropdown={
        <Dropdown
          icon={{ name: 'dots' }}
          offsetX={8}
          popoverProps={{
            offset: 20,
            className: 'absolute right-6 top-6',
          }}
          items={[
            {
              label: t('ui:removeFrame'),
              destructive: true,
              onPress: () => onRemove(id),
            },
          ]}
        />
      }
    />
  )
}

const FrameSelectedMemo = React.memo(FrameSelectItem)

const AdjustedFrameItem = ({ data, ...rest }: Props) => {
  const frame = useMemo(
    () => ({
      ...data.frame,
      measurement: data.measurement as FaceMeasurements,
      shape: {
        extend_x: Number(data.extend_x) || null,
        extend_y: Number(data.extend_y) || null,
      },
    }),
    [data]
  )

  const adjustedFrame = useAdjustedFrame(frame)

  return <FrameSelectedMemo frame={adjustedFrame} {...rest} id={data.id} />
}

export const AdjustedFrameItemMemo = React.memo(AdjustedFrameItem)

export default AdjustedFrameItemMemo
